import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgDialog,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";

import { usePostHistory } from "../../api/histories/post-history";
import {
  getDialogPropDetails,
  isDialogOpen,
  setDialog,
} from "../../store/slices/global";
import { contactSupport } from "../../utils/contactSupport";
import {
  campaignTesting,
  contentTestQaChecklist,
  endingThemeTest,
  priceTestIntegrationGettingStarted,
  priceTestQaChecklist,
  shippingTestQaChecklist,
  shippingTestSetup,
} from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";

interface ConfirmIgChecklistProps {
  onExperienceStart: (
    experiment: ExperienceDto,
    confirmedIntegration?: boolean
  ) => void;
  experience: ExperienceDto;
  onAdminOverrideClick: (
    experience: ExperienceDto,
    confirmedIntegration?: boolean
  ) => void;
}
export const ConfirmIgChecklist = ({
  onExperienceStart,
  experience,
  onAdminOverrideClick,
}: ConfirmIgChecklistProps) => {
  const isOpen = useSelector(isDialogOpen("confirmIgChecklist"));
  const dialogProps = useSelector(getDialogPropDetails("confirmIgChecklist"));

  const dispatch = useDispatch();
  const postHistory = usePostHistory();
  const isPostHistoryMutating = useIsMutating({
    mutationKey: ["postHistory"],
  });
  const [confirmChecklist, setConfirmChecklist] = useImmer({
    integration: experience.type.includes("content"),
    qa: false,
    themeTestEnd: experience.type !== "content/theme",
  });
  const handleClose = () => {
    setConfirmChecklist({ qa: false, integration: false, themeTestEnd: false });
    dispatch(
      setDialog({
        dialog: "confirmIgChecklist",
        open: false,
      })
    );
  };

  const integrationWording = useMemo(() => {
    if (experience.type.includes("content")) {
      return { link: null, label: null };
    } else if (experience.type === "pricing") {
      return {
        label: "price test integration",
        link: priceTestIntegrationGettingStarted,
      };
    } else if (experience.type === "shipping") {
      return {
        label: "shipping test integration",
        link: shippingTestSetup,
      };
    } else {
      return {
        label: "campaign test integration",
        link: campaignTesting,
      };
    }
  }, [experience]);

  return (
    <IgDialog
      title={"Confirm QA"}
      handleClose={handleClose}
      showDialog={isOpen}
      dialogContentWidth={"450px"}
      dialogHeight={null}
      maxWidth={"lg"}
      hideActions={true}
      rawContent={
        <Grid item container direction={"column"} rowSpacing={2}>
          {integrationWording?.label ? (
            <Grid item container direction={"row"}>
              <Grid item xs={1}>
                <IgCheckbox
                  disabled={Boolean(isPostHistoryMutating)}
                  sx={{ pt: 0 }}
                  onChange={(e) => {
                    setConfirmChecklist((draft) => {
                      draft.integration = e.target.checked;
                      return draft;
                    });
                  }}
                  checked={confirmChecklist.integration}
                />
              </Grid>
              <Grid item xs={11}>
                <IgTypography>
                  Check here to confirm that the{" "}
                  <IgTypography
                    igStyle={"link"}
                    onClick={() => {
                      openNewTab(integrationWording!.link!);
                    }}
                  >
                    {integrationWording.label}{" "}
                  </IgTypography>
                  has been completed for your store.
                </IgTypography>
              </Grid>
            </Grid>
          ) : experience.type === "content/theme" ? (
            <Grid item container direction={"row"}>
              <Grid item xs={1}>
                <IgCheckbox
                  disabled={Boolean(isPostHistoryMutating)}
                  sx={{ pt: 0 }}
                  onChange={(e) => {
                    setConfirmChecklist((draft) => {
                      draft.themeTestEnd = e.target.checked;
                      return draft;
                    });
                  }}
                  checked={confirmChecklist.themeTestEnd}
                />
              </Grid>
              <Grid item xs={11}>
                <IgTypography>
                  Check here to confirm you understand themes in theme tests{" "}
                  <IgTypography
                    igStyle={"link"}
                    onClick={() => {
                      openNewTab(endingThemeTest, "Ending a Theme Test");
                    }}
                  >
                    should not be deleted
                  </IgTypography>{" "}
                  for at least a month after a theme test has been ended.
                </IgTypography>
              </Grid>
            </Grid>
          ) : null}
          <Grid item container direction={"row"}>
            <Grid item xs={1}>
              <IgCheckbox
                disabled={Boolean(isPostHistoryMutating)}
                sx={{ pt: 0 }}
                onChange={(e) => {
                  setConfirmChecklist((draft) => {
                    draft.qa = e.target.checked;
                    return draft;
                  });
                }}
                checked={confirmChecklist.qa}
              />
            </Grid>
            <Grid item xs={11} sx={{ marginLeft: "-5px" }}>
              <IgTypography>
                Check here to confirm that you have properly QA'd this test
                <IgTypography
                  igStyle={
                    experience.type !== "personalization/offer"
                      ? "link"
                      : undefined
                  }
                  onClick={() => {
                    experience.type !== "personalization/offer" &&
                      openNewTab(
                        experience.type === "pricing"
                          ? priceTestQaChecklist
                          : experience.type === "shipping"
                            ? shippingTestQaChecklist
                            : contentTestQaChecklist
                      );
                  }}
                >
                  {experience.type !== "personalization/offer"
                    ? " (see checklist here)"
                    : ""}
                </IgTypography>
                .
              </IgTypography>
            </Grid>
          </Grid>
          <Grid item>
            <IgTypography>
              Have questions?{" "}
              <IgTypography onClick={contactSupport} igStyle={"link"}>
                Reach out to support.
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid item container direction={"column"} justifyContent={"center"}>
            <Grid item>
              <IgButton
                disabled={
                  !confirmChecklist.qa ||
                  !confirmChecklist.integration ||
                  !confirmChecklist.themeTestEnd
                }
                loading={Boolean(isPostHistoryMutating)}
                onClick={() => {
                  postHistory.mutate(
                    {
                      experienceId: experience.id!,
                      action: "confirmIntegration",
                    },
                    {
                      onSuccess: () => {
                        setConfirmChecklist({
                          qa: false,
                          integration: false,
                          themeTestEnd: false,
                        });

                        if (dialogProps.isSchedule) {
                          dispatch(
                            setDialog({
                              dialog: "scheduleTest",
                              open: true,
                              props: { experience },
                            })
                          );
                          handleClose();
                        } else {
                          dialogProps?.isAdminOverride
                            ? onAdminOverrideClick(experience)
                            : onExperienceStart(experience);
                          handleClose();
                        }
                      },
                    }
                  );
                }}
                variant={"contained"}
              >
                Confirm
              </IgButton>
            </Grid>
            {!isPostHistoryMutating && (
              <Grid item>
                <IgButton onClick={handleClose}>Cancel</IgButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      }
      yesButtonText={"Confirm"}
    />
  );
};
